import type { GatewayHelloOk } from "../gateway.ts";
import type { FleetProjectSummary, SessionsListResult } from "../types.ts";
import {
  resolveFleetProjectsContextSessionKey,
  resolveProjectOriginSessionKey,
} from "./fleet-projects-session.ts";
import { resolveAgentIdForSession } from "./resolve-session-agent.ts";
import { resolveAgentMainSessionKey } from "./session-catalog.ts";

function sessionKeysInList(sessions: SessionsListResult | null | undefined): Set<string> {
  const knownKeys = new Set<string>();
  const rows = sessions?.sessions;
  if (!Array.isArray(rows)) {
    return knownKeys;
  }
  for (const row of rows) {
    const key = row?.key?.trim();
    if (key) {
      knownKeys.add(key);
    }
  }
  return knownKeys;
}

/**
 * Gateway fleet.projects.create forks from a session that must exist in the store.
 * UI tabs may point at keys not yet listed — fall back to origin/main.
 */
export function resolveParentSessionKeyForFleetProject(params: {
  sessionKey: string;
  sessions: SessionsListResult | null;
  hello: GatewayHelloOk | null;
  fleetProjects: FleetProjectSummary[];
}): string {
  const key = params.sessionKey.trim();
  const known = sessionKeysInList(params.sessions);

  if (key && known.has(key)) {
    return key;
  }

  const contextKey = resolveFleetProjectsContextSessionKey(key, params.fleetProjects);
  if (contextKey && known.has(contextKey)) {
    return contextKey;
  }

  const asProject = params.fleetProjects.find((p) => p.sessionKey.trim() === key);
  if (asProject) {
    const origin = resolveProjectOriginSessionKey(asProject);
    if (known.has(origin)) {
      return origin;
    }
  }

  const agentId = resolveAgentIdForSession(key);
  const main = resolveAgentMainSessionKey(agentId, params.sessions, params.hello);
  if (main && known.has(main)) {
    return main;
  }

  if (main) {
    return main;
  }

  return `agent:${agentId}:main`;
}
